<template>
  <div class="forums-page">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex align-items-center justify-content-between">
            <h4 class="card-title mb-0">
              <i class="bi bi-chat-square-text me-2"></i>Forums
            </h4>
            <div class="d-flex gap-2 align-items-center">
              <!-- Search -->
              <div class="input-group" style="width: 300px;">
                <input
                  type="text"
                  class="form-control"
                  placeholder="Search forums..."
                  v-model="searchQuery"
                  @keyup.enter="searchForums"
                >
                <button class="btn btn-outline-secondary" @click="searchForums">
                  <i class="bi bi-search"></i>
                </button>
              </div>

              <!-- Working Group Filter -->
              <select class="form-select" v-model="selectedWorkingGroup" @change="loadForums" style="width: 200px;">
                <option value="">All Groups</option>
                <option v-for="group in workingGroups" :key="group.id" :value="group.id">
                  {{ group.name }}
                </option>
              </select>

              <!-- ✅ Only visible to Super Admin or EDCTP Admin -->
              <button
                v-if="isSuperAdmin || isEdctpAdmin"
                class="btn btn-primary"
                @click="showNewForumModal = true"
              >
                <i class="bi bi-plus me-2"></i>New Forum
              </button>
            </div>
          </div>
          <div class="card-body">
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
            <!-- Error State -->
            <div v-else-if="error" class="alert alert-danger">
              <i class="bi bi-exclamation-triangle me-2"></i>
              {{ error }}
              <button class="btn btn-sm btn-outline-danger ms-2" @click="loadForums">
                Try Again
              </button>
            </div>
            <!-- Forums List -->
            <div v-else-if="forums.length > 0" class="table-responsive">
              <table class="table table-hover">
                <thead>
                <tr>
                  <th>Subject</th>
                  <th>Author</th>
                  <th>Working Group</th>
                  <th>Topics</th>
                  <th>Last Activity</th>
                  <th>Actions</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="forum in displayForums" :key="forum.id">
                <td>
                    <div class="d-flex align-items-start">
                      <div class="flex-shrink-0 me-3">
                        <i class="bi bi-chat-square-text text-primary"></i>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="mb-1">
                          <a href="#" class="text-decoration-none" @click.prevent="viewForum(forum)">
                            {{ forum.subject }}
                          </a>
                        </h6>
                        <p class="text-muted small mb-0">{{ truncateText(forum.description, 100) }}</p>
                        <div class="d-flex gap-2 mt-1">
                          <span v-if="forum.enable_mod" class="badge bg-warning">Moderated</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex align-items-center">
                      <img :src="getAvatarUrl(forum.creator)" alt="Avatar" class="rounded-circle me-2" style="width: 32px; height: 32px;">
                      <div>
                        <div class="fw-semibold">{{ forum.creator?.name || 'Unknown' }}</div>
                        <small class="text-muted">{{ forum.creator?.email || '' }}</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class="badge bg-secondary">{{ forum.working_group?.name || 'No Group' }}</span>
                  </td>
                  <td>
                    <div class="text-center">
                      <span class="fw-bold">{{ forum.topics_count || 0 }}</span>
                    </div>
                  </td>
                  <td>
                    <div v-if="forum.last_activity">
                      <div class="fw-semibold">{{ forum.last_activity.time }}</div>
                      <small class="text-muted">by {{ forum.last_activity.user }}</small>
                    </div>
                    <div v-else>
                      <small class="text-muted">No activity yet</small>
                    </div>
                  </td>
                  <td>
                    <div class="dropdown">
                      <button
                        class="btn btn-sm btn-outline-secondary dropdown-toggle"
                        type="button"
                        :id="'dropdown-' + forum.id"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <i class="bi bi-three-dots"></i>
                      </button>
                      <ul class="dropdown-menu" :aria-labelledby="'dropdown-' + forum.id">
                        <li>
                          <button class="dropdown-item" @click="viewForum(forum)">
                            <i class="bi bi-eye me-2"></i>View
                          </button>
                        </li>
                        <li>
                          <button class="dropdown-item" @click="editForum(forum)">
                            <i class="bi bi-pencil-square me-2"></i>Edit
                          </button>
                        </li>
                      </ul>
                    </div>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
            <!-- Empty State -->
            <div v-else class="text-center py-5">
              <i class="bi bi-chat-square-text text-muted" style="font-size: var(--font-size-4xl);"></i>
              <h5 class="mt-3 text-muted">No forums found</h5>
              <p class="text-muted">
                {{ searchQuery ? 'Try adjusting your search criteria' : 'Join a working group to participate in the forums' }}
              </p>
              <button v-if="isSuperAdmin || isEdctpAdmin" class="btn btn-primary" @click="showNewForumModal = true">
                <i class="bi bi-plus me-2"></i>Create Forum
              </button>
            </div>
            <!-- Pagination -->
            <nav v-if="pagination && pagination.last_page > 1" class="mt-4">
              <ul class="pagination justify-content-center">
                <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                  <button class="page-link" @click="changePage(pagination.current_page - 1)" :disabled="pagination.current_page === 1">
                    Previous
                  </button>
                </li>
                <li
                  v-for="page in getPageNumbers()"
                  :key="page"
                  class="page-item"
                  :class="{ active: pagination.current_page === page }"
                >
                  <button class="page-link" @click="changePage(page)">{{ page }}</button>
                </li>
                <li class="page-item" :class="{ disabled: pagination.current_page === pagination.last_page }">
                  <button class="page-link" @click="changePage(pagination.current_page + 1)" :disabled="pagination.current_page === pagination.last_page">
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- New/Edit Forum Modal -->
    <div class="modal fade" :class="{ show: showNewForumModal, 'd-block': showNewForumModal }" v-if="showNewForumModal" data-bs-backdrop="static" data-bs-keyboard="false">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editingForum ? 'Edit Forum' : 'New Forum' }}</h5>
            <button
              type="button"
              class="btn-close"
              @click="closeForumModal"
            ></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveForum">
              <div class="mb-3">
                <label class="form-label">Working Group *</label>
                <select class="form-select" v-model="forumForm.working_group_id" required>
                  <option value="">Select Working Group</option>
                  <option v-for="group in workingGroups" :key="group.id" :value="group.id">
                    {{ group.name }}
                  </option>
                </select>
              </div>
              <div class="mb-3">
                <label class="form-label">Subject *</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="forumForm.subject"
                  required
                  maxlength="255"
                  placeholder="Enter forum subject"
                >
                <small class="form-text text-muted">{{ forumForm.subject.length }}/255 characters</small>
              </div>
              <div class="mb-3">
                <label class="form-label">Description *</label>
                <textarea
                  class="form-control"
                  rows="6"
                  v-model="forumForm.description"
                  required
                  placeholder="Enter forum description"
                ></textarea>
                <small class="form-text text-muted">Minimum 10 characters</small>
              </div>
              <div class="mb-3">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="forumForm.enable_mod" id="enableMod">
                  <label class="form-check-label" for="enableMod">
                    Enable moderation for this forum
                  </label>
                  <small class="form-text text-muted d-block">Posts will require approval before being visible</small>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeForumModal">Cancel</button>
            <button
              type="button"
              class="btn btn-primary"
              @click="saveForum"
              :disabled="saving || !isFormValid"
            >
              <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status"></span>
              {{ editingForum ? 'Update' : 'Create' }} Forum
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="modal fade show"
      style="display: block; background-color: rgba(0,0,0,0.5);"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-exclamation-triangle text-warning me-2"></i>
              Confirm Delete
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeForumModal"
            ></button>
          </div>
          <div class="modal-body">
            <p class="mb-0">
              Are you sure you want to delete this forum? This action cannot be undone and will also delete all associated topics and comments.
            </p>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="showDeleteModal = false"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-danger"
              @click="confirmDelete"
              :disabled="deleting"
            >
              <span v-if="deleting" class="spinner-border spinner-border-sm me-2" role="status"></span>
              <i class="bi bi-trash me-2"></i>
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 9999;">
      <div class="toast" :class="{ show: toast.show, 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error' }" role="alert">
        <div class="toast-header" :class="{ 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error', 'text-white': true }">
          <i class="bi me-2" :class="toast.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'"></i>
          <strong class="me-auto">{{ toast.type === 'success' ? 'Success' : 'Error' }}</strong>
          <button type="button" class="btn-close btn-close-white" @click="toast.show = false"></button>
        </div>
        <div class="toast-body text-white">
          {{ toast.message }}
        </div>
      </div>
    </div>

    <!-- Forum Detail Modal -->
    <ForumDetailModal
      :show="showForumDetailModal"
      :forum-id="selectedForumId"
      :forum="selectedForum"
      @close="closeForumDetailModal"
      @forum-updated="onForumUpdated"
    />
  </div>
</template>

<script>
import ForumService from '@/services/forums/ForumServices'
import AnalyticsService from '@/services/analytics/AnalyticsService'
import ForumDetailModal from './forums/ForumDetailModal.vue'
import UserService from "@/services/users/UserService"
import { avatarMixin } from '@/mixins/avatarMixin'

export default {
  name: 'Forums',
  mixins: [avatarMixin],
  components: {
    ForumDetailModal
  },
  data() {
    return {
      selectedForum: null,
      forums: [],
      workingGroups: [],
      pagination: null,
      loading: false,
      saving: false,
      deleting: false,
      error: null,
      searchQuery: '',
      selectedWorkingGroup: '',
      currentPage: 1,
      showNewForumModal: false,
      showDeleteModal: false,
      showForumDetailModal: false,
      forumToDelete: null,
      editingForum: null,
      selectedForumId: null,
      customWorkingGroup: '',
      forumForm: {
        subject: '',
        description: '',
        working_group_id: '',
        enable_mod: false
      },
      currentUserId: null,
      currentUserRoles: [],
      isSuperAdmin: false,
      isEdctpAdmin: false,
      toast: {
        show: false,
        type: 'success',
        message: ''
      },
    }
  },
  computed: {
    displayForums() {
      return this.forums
    },

    isFormValid() {
      return (
        this.forumForm.subject &&
        this.forumForm.subject.trim().length >= 3 &&
        this.forumForm.description &&
        this.forumForm.description.trim().length >= 10 &&
        this.forumForm.working_group_id
      )
    }
  },
  async mounted() {
    await this.loadWorkingGroups();
    await this.loadForums();
    await this.getCurrentUser();
  },
  methods: {
    showToast(type, message) {
      this.toast = {
        show: true,
        type: type,
        message: message
      }
      setTimeout(() => {
        this.toast.show = false
      }, 3000)
    },

    async getCurrentUser() {
      try {
        const response = await UserService.getCurrentUser()
        const user =
          response?.Data?.data?.user ||
          response?.data?.data?.user ||
          response?.data?.user ||
          response?.user ||
          null

        if (!user) {
          console.error("⚠️ No user found in API response", response)
          return null
        }

        const roles = user.roles?.map(r => r.name) || []
        this.currentUserId = user.id
        this.currentUserRoles = roles
        this.isSuperAdmin = roles.includes('Super Admin')
        this.isEdctpAdmin = roles.includes('EDCTP Admin')
        return user
      } catch (error) {
        console.error("❌ Error fetching current user:", error)
        return null
      }
    },
    async loadForums(page = 1) {
      this.loading = true;
      this.error = null;
      try {
        const params = {
          page,
          per_page: 15
        };
        if (this.selectedWorkingGroup) {
          params.working_group_id = this.selectedWorkingGroup;
        }
        if (this.searchQuery) {
          params.search = this.searchQuery;
        }
        const response = await ForumService.getForums(params);
        if (response && response.success) {
          const raw = response.data?.data || [];

          this.forums = raw.map(item => {
            // EDCTP wrapped forum
            if (item.forum && item.working_group) {
              return {
                ...item.forum,
                working_group: item.working_group
              };
            }

            // Normal forum
            return item;
          });
          this.pagination = {
            current_page: response.data?.current_page || 1,
            last_page: response.data?.last_page || 1,
            per_page: response.data?.per_page || 15,
            total: response.data?.total || 0
          };
        } else {
          this.error = response?.message || 'Failed to load forums';
          this.forums = [];
        }
      } catch (error) {
        console.error('Load Forums Error:', error);
        this.error = error.message || 'Failed to load forums';
        this.forums = [];
      } finally {
        this.loading = false;
      }
    },

    async loadWorkingGroups() {
      try {
        const response = await ForumService.getWorkingGroups();

        let data = [];

        if (response?.Data?.success && Array.isArray(response.Data.data)) {
          data = response.Data.data;
        } else if (response?.data?.success && Array.isArray(response.data.data)) {
          data = response.data.data;
        } else if (Array.isArray(response?.data)) {
          data = response.data;
        } else if (Array.isArray(response?.Data?.data)) {
          data = response.Data.data;
        } else if (Array.isArray(response)) {
          data = response;
        } else {
          console.warn('⚠️ Unexpected working groups response structure:', response);
        }

        this.workingGroups = data || [];

      } catch (error) {
        console.error('❌ Failed to load working groups:', error);
        this.workingGroups = [];
      }
    },

    async searchForums() {
      this.currentPage = 1;
      await this.loadForums(1);
    },

    async changePage(page) {
      if (page >= 1 && page <= this.pagination.last_page) {
        this.currentPage = page;
        await this.loadForums(page);
      }
    },

    getPageNumbers() {
      const pages = [];
      const current = this.pagination.current_page;
      const last = this.pagination.last_page;
      let start = Math.max(1, current - 2);
      let end = Math.min(last, start + 4);
      if (end - start < 4) {
        start = Math.max(1, end - 4);
      }
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },

    viewForum(forum) {
      this.selectedForum = forum;
      this.selectedForumId = forum.id || null;
      this.showForumDetailModal = true;
    },

    editForum(forum) {
      this.editingForum = forum;
      this.forumForm = {
        subject: forum.subject,
        description: forum.description,
        working_group_id: forum.working_group?.id,
        enable_mod: forum.enable_mod
      };
      this.showNewForumModal = true;
    },

    deleteForum(id) {
      this.forumToDelete = id;
      this.showDeleteModal = true;
    },

    async confirmDelete() {
      if (!this.forumToDelete) return;
      this.deleting = true;
      try {
        const response = await ForumService.deleteForum(this.forumToDelete);
        if (response && response.success) {
          this.showToast('success','Forum deleted successfully!');
          await this.loadForums(this.currentPage);
        } else {
          this.showToast('error',response?.message || 'Failed to delete forum');
        }
      } catch (error) {
        this.showToast('error',error.message || 'Failed to delete forum');
      } finally {
        this.deleting = false;
        this.showDeleteModal = false;
        this.forumToDelete = null;
      }
    },

    async saveForum() {
      if (!this.isFormValid) return;
      this.saving = true;
      try {
        const formData = { ...this.forumForm };

        let response;
        if (this.editingForum) {
          response = await ForumService.updateForum(this.editingForum.id, formData);
        } else {
          response = await ForumService.createForum(formData);
        }

        if (response && response.success) {
          this.showToast('success',`Forum ${this.editingForum ? 'updated' : 'created'} successfully!`);
          AnalyticsService.trackEvent(this.editingForum ? 'forum_update' : 'forum_create', {
            forum_id: this.editingForum ? this.editingForum.id : response.data?.id
          });
          this.closeForumModal();
          await this.loadForums(this.currentPage);
          await this.loadWorkingGroups();
        } else {
          this.showToast('error',response?.message || `Failed to ${this.editingForum ? 'update' : 'create'} forum`);
        }
      } catch (error) {
        console.error('❌ Forum Create Error:', error.response || error);
        this.showToast('error',error.message || 'Failed to create forum');
      } finally {
        this.saving = false;
      }
    },

    closeForumModal() {
      this.showNewForumModal = false;
      this.editingForum = null;
      this.customWorkingGroup = '';
      this.forumForm = {
        subject: '',
        description: '',
        working_group_id: '',
        enable_mod: false
      };
    },

    closeForumDetailModal() {
      this.showForumDetailModal = false;
      this.selectedForumId = null;
    },

    onForumUpdated() {
      // Refresh the forums list when a forum is updated from the detail modal
      this.loadForums(this.currentPage);
    },

    truncateText(text, length) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },

    getAvatarUrl(user) {
      return this.getUserAvatar(user);
    }
  }
}
</script>

<style scoped>
.forums-page {
  padding: 1rem 0;
}

.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
}

.badge {
  font-size: var(--font-size-xs);
}

.table th {
  background-color: #f8f9fa;
  border-top: none;
  font-weight: var(--font-weight-semibold);
}

.table td {
  vertical-align: middle;
}

.dropdown-toggle::after {
  display: none;
}

.dropdown-item {
  padding: 0.5rem 1rem;
  font-size: var(--font-size-sm);
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.dropdown-item.text-danger:hover {
  background-color: #f8d7da;
  color: #721c24;
}

.modal-content {
  border-radius: 8px;
  border: none;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-header {
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
  background-color: #f8f9fa;
  overflow: visible !important;
  z-index: 10;
}

.modal-title {
  color: #2c3e50;
  font-weight: var(--font-weight-semibold);
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  border-top: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
}

.btn-close:hover {
  opacity: 1;
}

.form-text {
  font-size: var(--font-size-sm);
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
</style>
